import React, { Component } from "react";
import CommonModal from "components/CommonModal";
import { connect, ConnectedProps } from "react-redux";
import { State } from "state";
import { fetchBasicInfoChains } from "state/chains";
import { fetchStores } from "state/stores";
import { Chain } from "interfaces/chain";
import { Store } from "interfaces/store";
import Loader from "components/Loader";
import { getRoles, Role } from "state/chain_user_role";
import Config from "config";
import axios from "axios";
import { createRequestAuditLog, updateRequestApprovalStatus } from "state/request_details_dependencies";
import { LightThemeTable } from "components/LightThemeTable";
import Constants from "helpers/constants";
import { SeverityLevel, ToastService } from "services/toastService";

interface AssignChainStoreModalProps extends PropsFromRedux {
    isOpen: boolean;
    onClose: () => void;
    stage: any;
    role?: string;
    onSuccess: () => void;
}

interface AssignChainStoreModalState {
    selectedChainId: number | null;
    selectedStores: number[];
    selectedRole: string;
    openChainDropdown: boolean;
    openStoreDropdown: boolean;
    openRoleDropdown: boolean;
    isSubmitting: boolean;
    isLoadingStores: boolean;
    filteredStores: Store[];
    chainSearchTerm: string;
    storeSearchTerm: string;
    roleSearchTerm: string;
    submitProgress: number;
    submitStep: string;
    apiErrorMessage: string | null;
    // per-user role overrides for users missing a role
    userRoleOverrides: { [email: string]: string };
    // per-user store overrides for users missing stores
    userStoreOverrides: { [email: string]: number[] };
    // per-user store search terms
    userStoreSearchTerms: { [email: string]: string };
    // per-user role validation errors
    roleValidationErrors: { [email: string]: boolean };
}

export interface AssignmentData {
    chainId: number;
    storeIds: number[];
    role: string;
    submitProgress: 0;
    submitStep: "";
}

const styles = {
    modalHeader: {
        display: "flex",
        alignItems: "center",
        gap: "16px",
        marginBottom: "10px",
    } as React.CSSProperties,
    modalMessage: {
        fontSize: "12px",
        color: "#666",
        marginBottom: "10px",
        lineHeight: 1.5,
    } as React.CSSProperties,
    modalContentWrapper: {
        maxHeight: "450px",
        overflowY: "auto" as const,
        overflowX: "hidden" as const,
        paddingRight: "5px",
    } as React.CSSProperties,
    modalLabel: {
        fontSize: "14px",
        fontWeight: 500,
        color: "#333",
    } as React.CSSProperties,
    buttonsContainer: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "12px",
        marginTop: "12px",
    } as React.CSSProperties,
    selectedStoresContainer: {
        marginTop: "12px",
        maxHeight: "150px",
        overflowY: "auto" as const,
        border: "1px solid #E0E0E0",
        borderRadius: "6px",
        padding: "8px",
        backgroundColor: "#FFFFFF",
    } as React.CSSProperties,
    selectedStoreChip: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        backgroundColor: "#E5B611",
        color: "#FFFFFF",
        padding: "6px 10px",
        borderRadius: "16px",
        fontSize: "12px",
        fontWeight: 500,
        margin: "4px",
    } as React.CSSProperties,
    removeStoreButton: {
        backgroundColor: "transparent",
        border: "none",
        color: "#FFFFFF",
        cursor: "pointer",
        fontSize: "16px",
        fontWeight: "bold",
        padding: "0",
        width: "16px",
        height: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        transition: "background-color 0.2s",
    } as React.CSSProperties,
    infoPill: {
        backgroundColor: "#E8F0FB",
        border: "1px solid #0755AA",
        borderRadius: "10px",
        padding: "10px 16px",
        fontSize: "12px",
        color: "#0755AA",
        fontWeight: 500,
        marginBottom: "10px",
        textAlign: "left" as const,
    },
    searchInput: {
        width: "100%",
        padding: "8px 12px",
        fontSize: "12px",
        border: "1px solid #E0E0E0",
        borderRadius: "4px",
        outline: "none",
        marginBottom: "4px",
    } as React.CSSProperties,
    emailBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        backgroundColor: "#FFF7E6",
        border: "1px solid #FFC266",
        color: "#7A4E00",
        padding: "6px 8px",
        borderRadius: "12px",
        fontSize: "12px",
        marginBottom: "0px",
    } as React.CSSProperties,
    tableSectionTitle: {
        fontSize: "13px",
        fontWeight: 600,
        color: "#333",
        marginBottom: "8px",
        marginTop: "0px",
    } as React.CSSProperties,
    tableSectionWrapper: {
        marginTop: "20px",
        backgroundColor: "#FFFFFF",
        borderRadius: "5px",
        padding: "16px",
        border: "1px solid #E0E0E0",
    } as React.CSSProperties,
    errorMessage: {
        fontSize: "11px",
        color: "#EF4444",
        margin: "4px 0 0 0",
    } as React.CSSProperties,
} as const;

class AssignChainStore extends Component<
    AssignChainStoreModalProps,
    AssignChainStoreModalState
> {
    private chainDropdownRef: React.RefObject<HTMLDivElement>;
    private storeDropdownRef: React.RefObject<HTMLDivElement>;
    private roleDropdownRef: React.RefObject<HTMLDivElement>;
    // Track open state of per-user store dropdowns without triggering full re-renders
    private openStoreDropdownPerUser: { [email: string]: boolean } = {};
    // Refs to trigger buttons so we can position the portal dropdown using getBoundingClientRect
    private storeDropdownAnchorRefs: { [email: string]: HTMLDivElement | null } = {};

    constructor(props: AssignChainStoreModalProps) {
        super(props);
        this.state = {
            selectedChainId: props.stage?.chainId ?? null,
            selectedStores: [],
            selectedRole: "all",
            openChainDropdown: false,
            openStoreDropdown: false,
            openRoleDropdown: false,
            isSubmitting: false,
            isLoadingStores: false,
            filteredStores: [],
            chainSearchTerm: "",
            storeSearchTerm: "",
            roleSearchTerm: "",
            submitProgress: 0,
            submitStep: "",
            apiErrorMessage: null,
            userRoleOverrides: {},
            userStoreOverrides: {},
            userStoreSearchTerms: {},
            roleValidationErrors: {},
        };
        this.chainDropdownRef = React.createRef();
        this.storeDropdownRef = React.createRef();
        this.roleDropdownRef = React.createRef();
    }

    componentDidMount() {
        document.addEventListener("click", this.handleClickOutside);
        this.props.fetchBasicInfoChains();
        this.props.getRoles();
        this.setState({ isLoadingStores: true });
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleClickOutside);
    }

    preFillUserStoreOverrides = () => {
        const { stage } = this.props;
        const { filteredStores, userStoreOverrides } = this.state;

        const updates: { [email: string]: number[] } = {};

        if (stage?.usersData && stage.usersData.length > 0) {
            stage.usersData.forEach(
                (u: { user_email: string; storeIds?: string[] }) => {
                    const email = this.resolveUserEmail(u.user_email);

                    // pre-fill stores
                    if (!userStoreOverrides[email] && Array.isArray(u.storeIds) && u.storeIds.length > 0) {
                        const matchedIds = filteredStores
                            .filter((s) => u.storeIds!.includes(s.retail_store_num))
                            .map((s) => s.id);

                        if (matchedIds.length > 0) {
                            updates[email] = matchedIds;
                        }
                    }
                }
            );
        } else {
            // Fallback: single user from stage.stores
            const email = this.resolveUserEmail();
            if (
                !userStoreOverrides[email] &&
                Array.isArray(stage?.stores) &&
                stage.stores.length > 0
            ) {
                const matchedIds = filteredStores
                    .filter((s) => stage.stores.includes(s.retail_store_num))
                    .map((s) => s.id);

                if (matchedIds.length > 0) {
                    updates[email] = matchedIds;
                }
            }
        }

        if (Object.keys(updates).length > 0) {
            this.setState((prev) => ({
                userStoreOverrides: { ...prev.userStoreOverrides, ...updates },
            }));
        }
    };
    componentDidUpdate(
        prevProps: AssignChainStoreModalProps,
        prevState: AssignChainStoreModalState
    ) {
        if (prevProps.stage?.chainId !== this.props.stage?.chainId) {
            this.setState({ selectedChainId: this.props.stage?.chainId ?? null });
        }

        if (this.props.isOpen && !prevProps.isOpen) {
            this.resetForm();
            if (!this.props.stores || this.props.stores.length === 0) {
                this.setState({ isLoadingStores: true });
                this.props.fetchStores({ page: 0, size: 100000000 });
            } else {
                this.updateFilteredStores();
            }
        }

        if (
            prevProps.stores !== this.props.stores ||
            prevState.selectedChainId !== this.state.selectedChainId
        ) {
            this.updateFilteredStores();
        }

        // Pre-populate userStoreOverrides when filteredStores becomes available
        if (
            prevState.filteredStores !== this.state.filteredStores &&
            this.state.filteredStores.length > 0
        ) {
            this.preFillUserStoreOverrides();
        }
    }

    updateFilteredStores = () => {
        const { selectedChainId } = this.state;
        const { stores = [], stage } = this.props;

        if (selectedChainId && stores.length > 0) {
            const filtered = stores.filter(
                (store: Store) => Number(store.chain_id) === Number(selectedChainId)
            );

            let preSelected: number[] = [];
            if (stage?.stores && Array.isArray(stage.stores) && stage.stores.length > 0) {
                preSelected = filtered
                    .filter((store) => stage.stores.includes(store.retail_store_num))
                    .map((store) => store.id);
            }

            this.setState((prevState) => ({
                filteredStores: filtered,
                isLoadingStores: false,
                selectedStores:
                    prevState.selectedStores.length === 0 && preSelected.length > 0
                        ? preSelected
                        : prevState.selectedStores,
            }));
        } else {
            this.setState({ filteredStores: [], isLoadingStores: false });
        }
    };

    handleChainFilterChange = (chainId: number | null) => {
        if (!chainId) return;

        const preSelectedRoleId =
            this.props.stage?.usersData?.length > 0 &&
                this.props.stage.usersData[0].user_role_id
                ? this.props.stage.usersData[0].user_role_id
                : "all";

        this.setState(
            {
                selectedChainId: chainId,
                openChainDropdown: false,
                selectedStores: [],
                selectedRole: preSelectedRoleId,
                isLoadingStores: true,
                chainSearchTerm: "",
                // Reset per-user store overrides when chain changes
                userStoreOverrides: {},
                userStoreSearchTerms: {},
            },
            () => {
                this.updateFilteredStores();
            }
        );
    };

    handleClickOutside = (event: MouseEvent) => {
        if (
            this.chainDropdownRef.current &&
            !this.chainDropdownRef.current.contains(event.target as Node)
        ) {
            this.setState({ openChainDropdown: false, chainSearchTerm: "" });
        }
        if (
            this.storeDropdownRef.current &&
            !this.storeDropdownRef.current.contains(event.target as Node)
        ) {
            this.setState({ openStoreDropdown: false, storeSearchTerm: "" });
        }
        if (
            this.roleDropdownRef.current &&
            !this.roleDropdownRef.current.contains(event.target as Node)
        ) {
            this.setState({ openRoleDropdown: false, roleSearchTerm: "" });
        }
    };

    resetForm = () => {
        const preSelectedRoleId =
            this.props.stage?.usersData?.length > 0 &&
                this.props.stage.usersData[0].user_role_id
                ? this.props.stage.usersData[0].user_role_id
                : "all";

        this.openStoreDropdownPerUser = {};
        this.storeDropdownAnchorRefs = {};

        this.setState({
            selectedChainId: this.props.stage?.chainId ?? null,
            selectedStores: [],
            selectedRole: preSelectedRoleId,
            openChainDropdown: false,
            openStoreDropdown: false,
            openRoleDropdown: false,
            isSubmitting: false,
            isLoadingStores: false,
            filteredStores: [],
            chainSearchTerm: "",
            storeSearchTerm: "",
            roleSearchTerm: "",
            userRoleOverrides: {},
            userStoreOverrides: {},
            userStoreSearchTerms: {},
            roleValidationErrors: {},
        });
    };

    handleStoreToggle = (storeId: number) => {
        const { selectedStores } = this.state;
        if (selectedStores.includes(storeId)) {
            this.setState({ selectedStores: selectedStores.filter((id) => id !== storeId) });
        } else {
            this.setState({ selectedStores: [...selectedStores, storeId] });
        }
    };

    handleSelectAllStores = () => {
        const { filteredStores, storeSearchTerm } = this.state;
        const filteredBySearch = this.filterStoresBySearch(filteredStores, storeSearchTerm);
        const allStoreIds = filteredBySearch.map((store) => store.id);
        this.setState({ selectedStores: allStoreIds });
    };

    handleClearAllStores = () => {
        this.setState({ selectedStores: [] });
    };

    handleRemoveStore = (storeId: number) => {
        this.setState({
            selectedStores: this.state.selectedStores.filter((id) => id !== storeId),
        });
    };

    handleRoleFilterChange = (value: string) => {
        this.setState({ selectedRole: value, openRoleDropdown: false, roleSearchTerm: "" });
    };

    // Handle per-user role change — also clears validation error
    handleUserRoleChange = (email: string, roleId: string) => {
        this.setState((prev) => ({
            userRoleOverrides: { ...prev.userRoleOverrides, [email]: roleId },
            roleValidationErrors: {
                ...prev.roleValidationErrors,
                [email]: false,
            },
        }));
    };

    // Per-user store handlers

    handleUserStoreToggle = (email: string, storeId: number) => {
        this.setState((prev) => {
            const current = prev.userStoreOverrides[email] || [];
            const updated = current.includes(storeId)
                ? current.filter((id) => id !== storeId)
                : [...current, storeId];
            return {
                userStoreOverrides: {
                    ...prev.userStoreOverrides,
                    [email]: updated,
                },
            };
        });
    };

    handleUserSelectAllStores = (email: string, storeOptions: Store[], searchTerm: string) => {
        const filtered = this.filterStoresBySearch(storeOptions, searchTerm);
        this.setState((prev) => ({
            userStoreOverrides: {
                ...prev.userStoreOverrides,
                [email]: filtered.map((s) => s.id),
            },
        }));
    };

    handleUserClearAllStores = (email: string) => {
        this.setState((prev) => ({
            userStoreOverrides: {
                ...prev.userStoreOverrides,
                [email]: [],
            },
        }));
    };

    handleUserStoreSearchChange = (email: string, term: string) => {
        this.setState((prev) => ({
            userStoreSearchTerms: {
                ...prev.userStoreSearchTerms,
                [email]: term,
            },
        }));
    };

    private resolveUserEmail = (userEmail?: string): string => {
        const { stage } = this.props;
        const candidateEmail = typeof userEmail === "string" ? userEmail.trim() : "";
        return candidateEmail || stage?.requestorEmail || "";
    };

    private resolveUserId = (userId?: number | string): number | string | null => {
        const { stage } = this.props;
        return userId || stage?.requestorId || null;
    };

    handleSubmit = async () => {
        const {
            selectedRole,
            filteredStores,
            selectedStores,
            selectedChainId,
            userRoleOverrides,
            userStoreOverrides,
        } = this.state;
        const { stage } = this.props;

        const roleOptions = this.getRoleOptions();
        const chainOptions = this.getChainOptions();

        const selectedChain = chainOptions.find((opt) => opt.value === selectedChainId);
        const chainName = selectedChain?.label ?? stage?.chainName ?? "";

        // Validate: every user must have a resolved role
        const roleErrors: { [email: string]: boolean } = {};
        let hasRoleErrors = false;

        const usersToValidate =
            stage.usersData && stage.usersData.length > 0
                ? stage.usersData.map((u: { user_email: string; user_role_id: number }) => {
                    // Use user_email if available, otherwise fall back to requestorEmail
                    const email = this.resolveUserEmail(u.user_email);
                    return {
                        email,
                        resolvedRoleId:
                            u.user_role_id ||
                            (userRoleOverrides[email]
                                ? Number(userRoleOverrides[email])
                                : null) ||
                            (selectedRole !== "all" ? Number(selectedRole) : null),
                    };
                })
                : [
                    {
                        email: this.resolveUserEmail(),
                        resolvedRoleId: (() => {
                            const email = this.resolveUserEmail();
                            return userRoleOverrides[email]
                                ? Number(userRoleOverrides[email])
                                : selectedRole !== "all"
                                    ? Number(selectedRole)
                                    : null;
                        })(),
                    },
                ];

        usersToValidate.forEach(
            ({ email, resolvedRoleId }: { email: string; resolvedRoleId: number | null }) => {
                const matched = roleOptions.find((r) => r.id === Number(resolvedRoleId));
                if (!matched) {
                    roleErrors[email] = true;
                    hasRoleErrors = true;
                }
            }
        );

        if (hasRoleErrors) {
            this.setState({ roleValidationErrors: roleErrors });
            return;
        }

        type UserRow = { email: string; storeNumbers: string[]; roleText: string };

        // Resolve stores per user:
        // Priority: global selectedStores → per-user userStoreOverrides → original user storeIds
        const resolveStoreNumbers = (
            email: string,
            originalStoreIds?: string[]
        ): string[] => {
            if (selectedStores.length > 0) {
                return selectedStores
                    .map((storeId) => {
                        const store = filteredStores.find((s) => s.id === storeId);
                        return store?.retail_store_num ?? "";
                    })
                    .filter(Boolean);
            }
            const perUserIds = userStoreOverrides[email];
            if (perUserIds && perUserIds.length > 0) {
                return perUserIds
                    .map((id) => filteredStores.find((s) => s.id === id)?.retail_store_num ?? "")
                    .filter(Boolean);
            }
            return Array.isArray(originalStoreIds) ? originalStoreIds.filter(Boolean) : [];
        };

        // Resolve role per user — check userRoleOverrides first, then user_role_id, then selectedRole
        const userRows: UserRow[] =
            stage.usersData && stage.usersData.length > 0
                ? stage.usersData.map(
                    (u: { user_email: string; user_role_id: number; storeIds?: string[] }) => {
                        // Use user_email if available, otherwise fall back to requestorEmail
                        const email = this.resolveUserEmail(u.user_email);
                        const overrideRoleId = userRoleOverrides[email];
                        const resolvedRoleId =
                            u.user_role_id ||
                            (overrideRoleId ? Number(overrideRoleId) : null) ||
                            (selectedRole !== "all" ? Number(selectedRole) : null);
                        const roleOpt = roleOptions.find((r) => r.id === Number(resolvedRoleId));
                        return {
                            email,
                            storeNumbers: resolveStoreNumbers(email, u.storeIds),
                            roleText: roleOpt?.role ?? "",
                        };
                    }
                )
                : [
                    {
                        email: this.resolveUserEmail(),
                        storeNumbers: resolveStoreNumbers(
                            this.resolveUserEmail(),
                            Array.isArray(stage?.stores) ? stage.stores : []
                        ),
                        roleText: (() => {
                            const email = this.resolveUserEmail();
                            const overrideRoleId = userRoleOverrides[email];
                            const effectiveRoleId = overrideRoleId
                                ? Number(overrideRoleId)
                                : selectedRole !== "all"
                                    ? Number(selectedRole)
                                    : null;
                            return (
                                roleOptions.find((r) => r.id === effectiveRoleId)?.role ?? ""
                            );
                        })(),
                    },
                ];

        this.setState({
            isSubmitting: true,
            submitProgress: 0,
            submitStep: "Performing store mapping...",
            apiErrorMessage: null,
        });

        try {
            // Step 1: store CSV → /api/users-stores
            const allStoreRows = userRows
                .flatMap((u) => u.storeNumbers.map((storeNum) => ({ email: u.email, storeNum })))
                .filter((r) => r.storeNum);

            if (allStoreRows.length > 0) {
                const headers = ["Chain Name", "Email Address", "Retail Store Number"];
                const csvRows = allStoreRows.map((r) =>
                    [`"${chainName}"`, `"${r.email}"`, `"${r.storeNum}"`].join(",")
                );
                const csvContent = [headers.join(","), ...csvRows].join("\n");
                const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
                const storeCSV = new File([blob], "User_Stores.csv", {
                    type: "text/csv",
                    lastModified: Date.now(),
                });

                const formData2 = new FormData();
                formData2.append("file", storeCSV);
                try {
                    await axios.post(`${Config.API_SERVICE_URL}/api/users-stores`, formData2, {
                        onUploadProgress: (progressEvent) => {
                            const percent = progressEvent.total
                                ? Math.round((progressEvent.loaded * 50) / progressEvent.total)
                                : 25;
                            this.setState({ submitProgress: percent });
                        },
                    });
                    await this.props.createRequestAuditLog(
                        stage.request_id,
                        formData2,
                        Constants.REQUEST_TASK_STORE_MAPPING,
                        stage.approval_stage_id
                    );
                } catch (error) {
                    const errorMessage = this.getErrorMessageFromError(error);
                    this.setState({ submitProgress: 0, apiErrorMessage: errorMessage });
                    ToastService.getInstance().publishToast(
                        {
                            identifier: "error-operation",
                            title: "Error",
                            message: errorMessage,
                        },
                        SeverityLevel.ERROR,
                        { autoClose: 5000 }
                    );
                    throw error;
                }
                
            } else {
                this.setState({ submitProgress: 50 });
            }

            // Step 2: chain-user-role CSV → /api/chain-user-role
            this.setState({ submitProgress: 50, submitStep: "Performing chain user roles mapping..." });

            const usersWithRoles = userRows
                .filter((u) => u.email && u.roleText)
                .map((u) => ({ email: u.email, role: u.roleText }));

            if (chainName && usersWithRoles.length > 0) {
                const chainRoleCSV = this.generateUserRolesCSV(chainName, usersWithRoles);
                const formData1 = new FormData();
                formData1.append("file", chainRoleCSV);
                try {
                    await axios.post(
                        `${Config.API_SERVICE_URL}/api/chain-user-role`,
                        formData1,
                        {
                            onUploadProgress: (progressEvent) => {
                                const percent = progressEvent.total
                                    ? 50 + Math.round((progressEvent.loaded * 50) / progressEvent.total)
                                    : 75;
                                this.setState({ submitProgress: percent });
                            },
                        }
                    );
                    await this.props.createRequestAuditLog(
                        stage.request_id,
                        formData1,
                        Constants.REQUEST_TASK_CHAIN_MAPPING,
                        stage.approval_stage_id
                    );
                } catch (error) {
                    const errorMessage = this.getErrorMessageFromError(error);
                    this.setState({ submitProgress: 0, apiErrorMessage: errorMessage });

                    ToastService.getInstance().publishToast(
                        {
                            identifier: "error-operation",
                            title: "Error",
                            message: errorMessage,
                        },
                        SeverityLevel.ERROR,
                        { autoClose: 5000 }
                    );
                    throw error;
                }
                
            } else {
                this.setState({ submitProgress: 100 });
            }

            await this.props.updateRequestApprovalStatus(stage.approval_stage_id, "COMPLETED", "", "");
            this.setState({ submitProgress: 100, submitStep: "Done!" });
            if (this.props.onSuccess) {
                this.props.onSuccess();
            }
            this.props.onClose();
        } catch (error) {
            console.error("Submit failed:", error);
            const errorMessage = this.getErrorMessageFromError(error);
            this.setState({ submitStep: "Submission failed. Please try again.", apiErrorMessage: errorMessage });
        } finally {
            this.setState({ isSubmitting: false });
        }
    };

    generateUserRolesCSV = (
        chainName: string,
        usersWithRoles: { email: string; role: string }[]
    ): File => {
        // /api/chain-user-role accepts only these three columns.
        const headers = ["Chain Name", "Email Address", "Role"];
        const csvRows = usersWithRoles.map((u) =>
            [`"${chainName}"`, `"${u.email}"`, `"${u.role}"`].join(",")
        );
        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        return new File([blob], `${chainName}_User_Roles.csv`, {
            type: "text/csv",
            lastModified: Date.now(),
        });
    };

    generateStoreUserCSV = (
        chainName: string,
        email: string,
        storeNumbers: string[]
    ): File => {
        const headers = ["Chain Name", "Email Address", "Retail Store Number"];
        const csvRows = storeNumbers.map((storeNum) =>
            [`"${chainName}"`, `"${email}"`, `"${storeNum}"`].join(",")
        );
        const csvContent = [headers.join(","), ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        return new File([blob], `User_Stores.csv`, { type: "text/csv", lastModified: Date.now() });
    };

    handleClose = () => {
        if (!this.state.isSubmitting) {
            this.resetForm();
            this.props.onClose();
        }
    };

    getErrorMessageFromError = (error: any): string => {
        return (
            error?.response?.data?.errors?.[0] ||
            error?.response?.data?.message ||
            error?.message ||
            "An unexpected error occurred. Please try again."
        );
    };

    filterStoresBySearch = (stores: Store[], searchTerm: string) => {
        if (!searchTerm.trim()) return stores;
        const lowerSearch = searchTerm.toLowerCase();
        return stores.filter(
            (store) =>
                store.name.toLowerCase().includes(lowerSearch) ||
                store.retail_store_num.toLowerCase().includes(lowerSearch)
        );
    };

    getChainOptions = () => {
        const { chains = [] } = this.props;
        return [
            { value: null, label: "Select Chain" },
            ...chains.map((chain: Chain) => ({ value: chain.id, label: chain.name })),
        ];
    };

    getStoreOptions = () => {
        const { filteredStores } = this.state;
        return filteredStores.map((store: Store) => ({
            value: store.id,
            label: `${store.retail_store_num}`.trim(),
        }));
    };

    getRoleOptions = () => {
        return this.props.roles || [];
    };

    private getSelectedChainLabel = () => {
        const { selectedChainId } = this.state;
        const { chains = [], stage } = this.props;
        if (!selectedChainId) return "Select Chain";
        const matched = chains.find((c: Chain) => Number(c.id) === Number(selectedChainId));
        if (matched?.name) return matched.name;
        if (stage?.chainName) return stage.chainName;
        return `Chain #${selectedChainId}`;
    };

    private buildTableData = (
        selectedChainLabel: string,
        selectedRole: string,
        roleOptions: Role[],
        effectiveRole: string | number
    ) => {
        const { selectedStores, filteredStores, userRoleOverrides, userStoreOverrides } = this.state;
        const { stage } = this.props;
        const chainLabel = selectedChainLabel !== "Select Chain" ? selectedChainLabel : "—";

        // Helper: resolve store numbers for a user
        const resolveStoreNums = (email: string, originalStoreIds?: string[]): string[] => {
            if (selectedStores.length > 0) {
                return selectedStores
                    .map((storeId) => {
                        const store = filteredStores.find((s) => s.id === storeId);
                        return store?.retail_store_num ?? "";
                    })
                    .filter(Boolean);
            }
            const perUserIds = userStoreOverrides[email];
            if (perUserIds && perUserIds.length > 0) {
                return perUserIds
                    .map((id) => filteredStores.find((s) => s.id === id)?.retail_store_num ?? "")
                    .filter(Boolean);
            }
            return Array.isArray(originalStoreIds) ? originalStoreIds.filter(Boolean) : [];
        };

        if (stage.usersData && stage.usersData.length > 0) {
            return stage.usersData.map(
                (u: { user_email: string; user_role_id: number; storeIds?: string[] }) => {
                    // Use user_email if available, otherwise fall back to requestorEmail
                    const email = this.resolveUserEmail(u.user_email);
                    const overrideRoleId = userRoleOverrides[email];
                    const resolvedRoleId =
                        u.user_role_id ||
                        (overrideRoleId ? Number(overrideRoleId) : null) ||
                        (selectedRole !== "all" ? Number(selectedRole) : null);
                    const roleOption = roleOptions.find((r) => r.id === Number(resolvedRoleId));
                    const roleName = roleOption?.role ?? null;

                    const userStoreNums = resolveStoreNums(email, u.storeIds);
                    // Original stores from data (before any override)
                    const originalStoreNums = Array.isArray(u.storeIds)
                        ? u.storeIds.filter(Boolean)
                        : [];

                    return {
                        email,
                        chain: chainLabel,
                        storeIds: userStoreNums,
                        originalStoreIds: originalStoreNums,
                        role: roleName,
                        hasRoleAssigned: !!u.user_role_id,
                        // Stores are "assigned" if they came from original data (not needing override)
                        hasStoresAssigned: originalStoreNums.length > 0,
                    };
                }
            );
        }

        // Fallback: single row using requestorEmail / requestorId
        const fallbackEmail = this.resolveUserEmail();
        const overrideRoleId = userRoleOverrides[fallbackEmail];
        const effectiveRoleId = overrideRoleId
            ? Number(overrideRoleId)
            : Number(effectiveRole) || null;
        const selectedRoleOption = roleOptions.find((r) => r.id === effectiveRoleId);
        const roleName = selectedRoleOption?.role ?? null;

        const originalStores = Array.isArray(stage?.stores) ? stage.stores.filter(Boolean) : [];
        const storeNums = resolveStoreNums(fallbackEmail, originalStores);

        return fallbackEmail
            ? [
                {
                    email: fallbackEmail,
                    chain: chainLabel,
                    storeIds: storeNums,
                    originalStoreIds: originalStores,
                    role: roleName,
                    hasRoleAssigned: false,
                    hasStoresAssigned: originalStores.length > 0,
                },
            ]
            : [];
    };

    // Inline per-user store multi-select renderer
    private renderUserStoreDropdown = (row: any) => {
        const { filteredStores, userStoreOverrides, userStoreSearchTerms, isLoadingStores } = this.state;
        const email = row.email;
        const selectedIds = userStoreOverrides[email] || [];
        const searchTerm = userStoreSearchTerms[email] || "";
        const isOpen = !!this.openStoreDropdownPerUser[email];

        const availableStores = filteredStores;
        const filteredBySearch = this.filterStoresBySearch(availableStores, searchTerm);
        const allSelected =
            filteredBySearch.length > 0 && selectedIds.length === filteredBySearch.length;

        const toggleDropdown = () => {
            this.openStoreDropdownPerUser[email] = !isOpen;
            this.forceUpdate();
        };

        const closeDropdown = () => {
            this.openStoreDropdownPerUser[email] = false;
            this.forceUpdate();
        };

        // Compute fixed position from trigger ref so the panel escapes any
        // overflow:hidden / overflow:auto ancestor ( modal scroll container).
        const getDropdownStyle = (): React.CSSProperties => {
            const anchor = this.storeDropdownAnchorRefs[email];
            if (!anchor) {
                return {
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: 240,
                };
            }
            const rect = anchor.getBoundingClientRect();
            const dropdownHeight = 240;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < dropdownHeight + 8 && rect.top > dropdownHeight;

            return {
                position: "fixed",
                top: openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
                left: rect.left,
                width: Math.max(rect.width, 240),
                zIndex: 9999,
                border: "1px solid #D1D5DB",
                borderRadius: 6,
                backgroundColor: "#fff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                maxHeight: dropdownHeight,
                overflowY: "auto",
            };
        };

        return (
            <div style={{ padding: "4px 8px", minWidth: "200px" }}>
                {/* Dropdown trigger */}
                <div
                    ref={(el) => { this.storeDropdownAnchorRefs[email] = el; }}
                    onClick={isLoadingStores ? undefined : toggleDropdown}
                    style={{
                        borderRadius: 6,
                        fontSize: 12,
                        height: 32,
                        border: "1px solid #FFC266",
                        backgroundColor: isLoadingStores ? "#F9FAFB" : "#FFFBF0",
                        cursor: isLoadingStores ? "not-allowed" : "pointer",
                        padding: "0 32px 0 10px",
                        color: selectedIds.length > 0 ? "#333" : "#9CA3AF",
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        userSelect: "none",
                        opacity: isLoadingStores ? 0.6 : 1,
                        transition: "border-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                        if (!isLoadingStores)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#E5A000";
                    }}
                    onMouseLeave={(e) => {
                        if (!isLoadingStores)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#FFC266";
                    }}
                >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {selectedIds.length > 0
                            ? `${selectedIds.length} store(s) selected`
                            : "Select Store(s)"}
                    </span>
                    <span
                        style={{
                            position: "absolute",
                            right: 10,
                            fontSize: 16,
                            color: "#9CA3AF",
                            transform: `rotate(${isOpen ? "270deg" : "90deg"})`,
                            transition: "transform 0.2s",
                            fontWeight: 600,
                            pointerEvents: "none",
                        }}
                    >
                        ›
                    </span>
                </div>

                {isLoadingStores && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        marginTop: 4,
                        fontSize: 11,
                        color: "#E5B611",
                    }}>
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            style={{ animation: "spin 1s linear infinite", flexShrink: 0 }}
                        >
                            <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
                            <circle cx="6" cy="6" r="4" stroke="#E5B611" strokeWidth="2"
                                fill="none" strokeDasharray="18" strokeLinecap="round" />
                        </svg>
                        <span>Fetching stores...</span>
                    </div>
                )}

                {isOpen && !isLoadingStores && (
                    <>
                        {/* Full-screen backdrop — closes dropdown on outside click */}
                        <div
                            style={{ position: "fixed", inset: 0, zIndex: 9998 }}
                            onClick={closeDropdown}
                        />
                        <div style={getDropdownStyle()} onClick={(e) => e.stopPropagation()}>
                            {/* Search */}
                            <div style={{ padding: "8px", borderBottom: "1px solid #E3E6EA" }}>
                                <input
                                    type="text"
                                    placeholder="Search stores..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                        this.handleUserStoreSearchChange(email, e.target.value)
                                    }
                                    autoFocus
                                    style={{ ...styles.searchInput, marginBottom: 0 }}
                                />
                            </div>

                            {/* Select All / Clear All */}
                            {availableStores.length > 0 && (
                                <div
                                    onClick={() =>
                                        allSelected
                                            ? this.handleUserClearAllStores(email)
                                            : this.handleUserSelectAllStores(email, availableStores, searchTerm)
                                    }
                                    style={{
                                        fontSize: 12,
                                        padding: "8px 12px",
                                        cursor: "pointer",
                                        color: "#E5B611",
                                        fontWeight: 600,
                                        borderBottom: "1px solid #E3E6EA",
                                        backgroundColor: "#fff",
                                    }}
                                    onMouseEnter={(e) =>
                                        ((e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5")
                                    }
                                    onMouseLeave={(e) =>
                                        ((e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff")
                                    }
                                >
                                    {allSelected ? "Clear All" : `Select All${searchTerm ? " (Filtered)" : ""}`}
                                </div>
                            )}

                            {/* Options */}
                            {filteredBySearch.length === 0 ? (
                                <div style={{ fontSize: 12, padding: "10px 12px", color: "#999", textAlign: "center" }}>
                                    {availableStores.length === 0
                                        ? "No stores available for this chain"
                                        : "No results found"}
                                </div>
                            ) : (
                                filteredBySearch.map((store) => {
                                    const isSelected = selectedIds.includes(store.id);
                                    return (
                                        <div
                                            key={store.id}
                                            onClick={() => this.handleUserStoreToggle(email, store.id)}
                                            style={{
                                                fontSize: 12,
                                                padding: "8px 12px",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 8,
                                                backgroundColor: "#fff",
                                            }}
                                            onMouseEnter={(e) =>
                                                ((e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5")
                                            }
                                            onMouseLeave={(e) =>
                                                ((e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff")
                                            }
                                        >
                                            <div
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    border: `2px solid ${isSelected ? "#E5B611" : "#D1D5DB"}`,
                                                    borderRadius: 3,
                                                    backgroundColor: isSelected ? "#E5B611" : "#fff",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                    transition: "all 0.2s",
                                                }}
                                            >
                                                {isSelected && (
                                                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                                                        <path d="M1 4L3.5 6.5L9 1" stroke="white"
                                                            strokeWidth="2" strokeLinecap="round"
                                                            strokeLinejoin="round" />
                                                    </svg>
                                                )}
                                            </div>
                                            <span>{store.retail_store_num}</span>
                                            {store.name && (
                                                <span style={{ color: "#9CA3AF", fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {store.name}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}

                {/* Selected chips — shown below trigger */}
                {selectedIds.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {selectedIds.map((id) => {
                            const store = filteredStores.find((s) => s.id === id);
                            return (
                                <span
                                    key={id}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 4,
                                        backgroundColor: "#E5B611",
                                        color: "#fff",
                                        padding: "3px 8px",
                                        borderRadius: 12,
                                        fontSize: 11,
                                        fontWeight: 500,
                                    }}
                                >
                                    {store?.retail_store_num ?? `#${id}`}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            this.handleUserStoreToggle(email, id);
                                        }}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "#fff",
                                            cursor: "pointer",
                                            fontSize: 14,
                                            lineHeight: 1,
                                            padding: 0,
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        ×
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* when no store selected and not loading */}
                {selectedIds.length === 0 && !isLoadingStores && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
                        <span
                            style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                border: "1.5px solid #E5A000",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 9,
                                fontWeight: "bold",
                                color: "#E5A000",
                                flexShrink: 0,
                            }}
                        >
                            !
                        </span>
                        <span style={{ fontSize: 11, color: "#E5A000" }}>Store selection required</span>
                    </div>
                )}
            </div>
        );
    };

    // Role column + Store column definitions
    private getTableColumns = () => {
        const roleOptions = this.getRoleOptions();
        const { userRoleOverrides, roleValidationErrors } = this.state;
        return [
            {
                field: "email",
                header: "Email",
                formatter: (_: any, row: any) => (
                    <div style={{ padding: "8px", fontSize: "12px" }}>{row.email}</div>
                ),
            },
            {
                field: "storeIds",
                header: "Store Numbers",
                formatter: (_: any, row: any) => {
                    const isSystemAdmin = this.props.user?.role === Constants.AB_SYSTEM_ADMIN; 

                    // System admin with stores already assigned — show as plain read-only text
                    if (row.hasStoresAssigned && row.storeIds.length > 0 && isSystemAdmin) {
                        return (
                            <div
                                style={{
                                    padding: "8px",
                                    fontSize: "12px",
                                    maxWidth: "220px",
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                }}
                            >
                                {row.storeIds.join(", ")}
                            </div>
                        );
                    }

                    // Non-system-admin OR no stores assigned — show editable inline store picker
                    return this.renderUserStoreDropdown(row);
                },
            },
            {
                field: "role",
                header: "Role",
                formatter: (_: any, row: any) => {
                    // If role is already assigned from server, display as plain text
                    if (row.hasRoleAssigned && row.role) {
                        return (
                            <div style={{ padding: "8px", fontSize: "12px" }}>{row.role}</div>
                        );
                    }

                    // Role is missing — show inline select dropdown
                    const currentOverride = userRoleOverrides[row.email] || "";
                    const hasValidationError = !!roleValidationErrors[row.email];

                    return (
                        <div style={{ padding: "4px 8px" }}>
                            <select
                                style={{
                                    padding: "6px 10px",
                                    border: `1px solid ${hasValidationError
                                        ? "#EF4444"
                                        : currentOverride
                                            ? "#D1D5DB"
                                            : "#FFC266"
                                        }`,
                                    borderRadius: "6px",
                                    fontSize: "12px",
                                    color: currentOverride ? "#374151" : "#9CA3AF",
                                    cursor: "pointer",
                                    backgroundColor: hasValidationError
                                        ? "#FFF5F5"
                                        : currentOverride
                                            ? "#fff"
                                            : "#FFFBF0",
                                    minWidth: "160px",
                                    outline: "none",
                                }}
                                value={currentOverride}
                                onChange={(e) =>
                                    this.handleUserRoleChange(row.email, e.target.value)
                                }
                            >
                                <option value="" disabled>
                                    Select Role
                                </option>
                                {roleOptions.map((role) => (
                                    <option key={role.id} value={String(role.id)}>
                                        {role.role}
                                    </option>
                                ))}
                            </select>

                            {hasValidationError && (
                                <p style={styles.errorMessage}>Role is required</p>
                            )}

                            {/* before submit when no role chosen yet */}
                            {!hasValidationError && !currentOverride && (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        marginTop: "4px",
                                    }}
                                >
                                    <span
                                        style={{
                                            width: 12,
                                            height: 12,
                                            borderRadius: "50%",
                                            border: "1.5px solid #E5A000",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 9,
                                            fontWeight: "bold",
                                            color: "#E5A000",
                                            flexShrink: 0,
                                        }}
                                    >
                                        !
                                    </span>
                                    <span style={{ fontSize: "11px", color: "#E5A000" }}>
                                        Role required
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                },
            },
        ];
    };

    renderSelect = (
        label: string,
        value: number | string | null,
        isOpen: boolean,
        onToggle: () => void,
        onSelect: (value: any) => void,
        options: Array<{ value: any; label: string }>,
        dropdownRef: React.RefObject<HTMLDivElement>,
        disabled: boolean = false,
        searchTerm: string = "",
        onSearchChange: (term: string) => void
    ) => {
        const selectedOption =
            value !== null && value !== "all"
                ? options.find((opt) => opt.value === value)
                : null;
        const displayValue = selectedOption ? selectedOption.label : "";

        let filteredOptions = options.filter(
            (opt) => opt.value !== null && opt.value !== "all"
        );
        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.toLowerCase();
            filteredOptions = filteredOptions.filter((opt) =>
                opt.label.toLowerCase().includes(lowerSearch)
            );
        }

        return (
            <div style={{ position: "relative" }} ref={dropdownRef}>
                <div
                    style={{
                        borderRadius: 15,
                        fontSize: 12,
                        height: 32,
                        border: "1.5px solid #D1D5DB",
                        backgroundColor: disabled ? "#F9FAFB" : "#fff",
                        cursor: disabled ? "not-allowed" : "pointer",
                        padding: "0 35px 0 12px",
                        color: disabled ? "#D1D5DB" : displayValue ? "#333" : "#999",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        opacity: disabled ? 0.6 : 1,
                    }}
                    onClick={disabled ? undefined : onToggle}
                    onMouseEnter={(e) => {
                        if (!disabled)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#888";
                    }}
                    onMouseLeave={(e) => {
                        if (!disabled)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#D1D5DB";
                    }}
                >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {displayValue || label}
                    </span>
                    <span
                        style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: `translateY(-50%) rotate(${isOpen ? "270deg" : "90deg"})`,
                            color: disabled ? "#D1D5DB" : "#888",
                            fontSize: 18,
                            pointerEvents: "none",
                            transition: "transform 0.2s ease",
                            fontWeight: 600,
                        }}
                    >
                        ›
                    </span>
                </div>

                {isOpen && !disabled && (
                    <div
                        style={{
                            position: "absolute",
                            top: 42,
                            left: 0,
                            right: 0,
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            backgroundColor: "#fff",
                            zIndex: 10,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            maxHeight: 250,
                            overflowY: "auto",
                        }}
                    >
                        <div style={{ padding: "8px", borderBottom: "1px solid #E3E6EA" }}>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onSearchChange(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.searchInput}
                                autoFocus
                            />
                        </div>

                        {value !== null && value !== "all" && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(typeof value === "number" ? null : "all");
                                }}
                                style={{
                                    fontSize: 12,
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    backgroundColor: "#fff",
                                    color: "#999",
                                    borderBottom: "1px solid #E3E6EA",
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor = "#fff";
                                }}
                            >
                                Clear selection
                            </div>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    padding: "8px 16px",
                                    color: "#999",
                                    textAlign: "center",
                                }}
                            >
                                No results found
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.value}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelect(option.value);
                                    }}
                                    style={{
                                        fontSize: 12,
                                        padding: "8px 16px",
                                        cursor: "pointer",
                                        backgroundColor:
                                            value === option.value ? "#f5f5f5" : "#fff",
                                    }}
                                    onMouseEnter={(e) => {
                                        (e.currentTarget as HTMLDivElement).style.backgroundColor =
                                            "#f5f5f5";
                                    }}
                                    onMouseLeave={(e) => {
                                        if (value !== option.value)
                                            (
                                                e.currentTarget as HTMLDivElement
                                            ).style.backgroundColor = "#fff";
                                    }}
                                >
                                    {option.label}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        );
    };

    renderMultiSelect = (
        label: string,
        selectedValues: number[],
        isOpen: boolean,
        onToggle: () => void,
        onToggleItem: (value: number) => void,
        options: Array<{ value: number; label: string }>,
        dropdownRef: React.RefObject<HTMLDivElement>,
        disabled: boolean = false,
        searchTerm: string = "",
        onSearchChange: (term: string) => void
    ) => {
        const displayText =
            selectedValues.length > 0
                ? `${selectedValues.length} store(s) selected`
                : label;

        let filteredOptions = options;
        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.toLowerCase();
            filteredOptions = options.filter((opt) =>
                opt.label.toLowerCase().includes(lowerSearch)
            );
        }

        const allSelected =
            selectedValues.length === filteredOptions.length && filteredOptions.length > 0;

        return (
            <div style={{ position: "relative" }} ref={dropdownRef}>
                <div
                    style={{
                        borderRadius: 15,
                        fontSize: 12,
                        height: 32,
                        border: "1.5px solid #D1D5DB",
                        backgroundColor: disabled ? "#F9FAFB" : "#fff",
                        cursor: disabled ? "not-allowed" : "pointer",
                        padding: "0 35px 0 12px",
                        color: disabled
                            ? "#D1D5DB"
                            : selectedValues.length > 0
                                ? "#333"
                                : "#999",
                        display: "flex",
                        alignItems: "center",
                        transition: "all 0.2s",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        opacity: disabled ? 0.6 : 1,
                    }}
                    onClick={disabled ? undefined : onToggle}
                    onMouseEnter={(e) => {
                        if (!disabled)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#888";
                    }}
                    onMouseLeave={(e) => {
                        if (!disabled)
                            (e.currentTarget as HTMLDivElement).style.borderColor = "#D1D5DB";
                    }}
                >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {displayText}
                    </span>
                    <span
                        style={{
                            position: "absolute",
                            right: 12,
                            top: "50%",
                            transform: `translateY(-50%) rotate(${isOpen ? "270deg" : "90deg"})`,
                            color: disabled ? "#D1D5DB" : "#888",
                            fontSize: 18,
                            pointerEvents: "none",
                            transition: "transform 0.2s ease",
                            fontWeight: 600,
                        }}
                    >
                        ›
                    </span>
                </div>

                {isOpen && !disabled && (
                    <div
                        style={{
                            position: "absolute",
                            top: 42,
                            left: 0,
                            right: 0,
                            border: "1px solid #ccc",
                            borderRadius: 6,
                            backgroundColor: "#fff",
                            zIndex: 10,
                            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                            maxHeight: 250,
                            overflowY: "auto",
                        }}
                    >
                        <div style={{ padding: "8px", borderBottom: "1px solid #E3E6EA" }}>
                            <input
                                type="text"
                                placeholder="Search stores..."
                                value={searchTerm}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onSearchChange(e.target.value);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={styles.searchInput}
                                autoFocus
                            />
                        </div>

                        {options.length > 0 && (
                            <div
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (allSelected) {
                                        this.handleClearAllStores();
                                    } else {
                                        this.handleSelectAllStores();
                                    }
                                }}
                                style={{
                                    fontSize: 12,
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    backgroundColor: "#fff",
                                    color: "#E5B611",
                                    fontWeight: 600,
                                    borderBottom: "1px solid #E3E6EA",
                                }}
                                onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                                        "#f5f5f5";
                                }}
                                onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLDivElement).style.backgroundColor =
                                        "#fff";
                                }}
                            >
                                {allSelected
                                    ? "Clear All"
                                    : `Select All${searchTerm ? " (Filtered)" : ""}`}
                            </div>
                        )}

                        {filteredOptions.length === 0 ? (
                            <div
                                style={{
                                    fontSize: 12,
                                    padding: "8px 16px",
                                    color: "#999",
                                    textAlign: "center",
                                }}
                            >
                                {options.length === 0
                                    ? "No stores available for this chain"
                                    : "No results found"}
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = selectedValues.includes(option.value);
                                return (
                                    <div
                                        key={option.value}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleItem(option.value);
                                        }}
                                        style={{
                                            fontSize: 12,
                                            padding: "8px 16px",
                                            cursor: "pointer",
                                            backgroundColor: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "8px",
                                        }}
                                        onMouseEnter={(e) => {
                                            (
                                                e.currentTarget as HTMLDivElement
                                            ).style.backgroundColor = "#f5f5f5";
                                        }}
                                        onMouseLeave={(e) => {
                                            (
                                                e.currentTarget as HTMLDivElement
                                            ).style.backgroundColor = "#fff";
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "16px",
                                                height: "16px",
                                                border: `2px solid ${isSelected ? "#E5B611" : "#D1D5DB"
                                                    }`,
                                                borderRadius: "3px",
                                                backgroundColor: isSelected ? "#E5B611" : "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            {isSelected && (
                                                <svg
                                                    width="10"
                                                    height="8"
                                                    viewBox="0 0 10 8"
                                                    fill="none"
                                                >
                                                    <path
                                                        d="M1 4L3.5 6.5L9 1"
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <span>{option.label}</span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                )}
            </div>
        );
    };

    render() {
        const {
            selectedRole,
            isSubmitting,
            submitProgress,
            submitStep,
            apiErrorMessage,
        } = this.state;

        const roleOptions = this.getRoleOptions();
        const { stage } = this.props;
        const preSelectedRoleId: string | number =
            stage.usersData &&
                stage.usersData.length > 0 &&
                stage.usersData[0].user_role_id
                ? stage.usersData[0].user_role_id
                : "all";

        const effectiveRole =
            selectedRole === "all" && preSelectedRoleId !== "all"
                ? preSelectedRoleId
                : selectedRole;


        const selectedChainLabel = this.getSelectedChainLabel();

        const tableData = this.buildTableData(
            selectedChainLabel,
            selectedRole,
            roleOptions,
            effectiveRole
        );
        const tableColumns = this.getTableColumns();

        const modalHeaderContent = (
            <div style={styles.modalHeader}>
                <h2 style={{ fontWeight: 500, fontSize: "16px" }}>Assign Chain and Store</h2>
            </div>
        );

        const modalFooter = (
            <div style={{ width: "100%" }}>
                {isSubmitting && (
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, color: "#555", marginBottom: 4 }}>
                            {submitStep}
                        </div>
                        <div
                            style={{
                                height: 8,
                                borderRadius: 4,
                                background: "#e0e0e0",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    height: "100%",
                                    width: `${submitProgress}%`,
                                    background: "#E5B611",
                                    borderRadius: 4,
                                    transition: "width 0.3s ease",
                                }}
                            />
                        </div>
                        <div
                            style={{
                                textAlign: "right",
                                fontSize: 12,
                                color: "#888",
                                marginTop: 4,
                            }}
                        >
                            {submitProgress}%
                        </div>
                    </div>
                )}
                <div style={styles.buttonsContainer}>
                    <button
                        className="primary-button"
                        onClick={this.handleClose}
                        disabled={isSubmitting}
                        style={{ minWidth: "120px" }}
                    >
                        Cancel
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <span
                            style={{
                                display: "inline-flex",
                                width: 20,
                                height: 20,
                                alignItems: "center",
                                justifyContent: "center",
                                visibility: isSubmitting ? "visible" : "hidden",
                            }}
                            aria-hidden={!isSubmitting}
                        >
                            {isSubmitting && <Loader height={20} width={20} custom={true} />}
                        </span>
                        <button
                            className="primary-button"
                            onClick={this.handleSubmit}
                            disabled={isSubmitting}
                            aria-label={isSubmitting ? "Submitting request" : "Submit request"}
                            aria-busy={isSubmitting || undefined}
                            style={{
                                opacity: isSubmitting ? 0.5 : 1,
                                cursor: isSubmitting ? "not-allowed" : "pointer",
                                minWidth: "120px",
                            }}
                        >
                            {isSubmitting ? " Approving..." : "Approve"}
                        </button>
                    </div>
                </div>
            </div>
        );

        return (
            <CommonModal
                show={this.props.isOpen}
                onClose={this.handleClose}
                title=""
                subTitle=""
                width="900px"
                closeOnBackdropClick={!isSubmitting}
                footer={modalFooter}
            >
                {modalHeaderContent}

                <p style={styles.modalMessage}>
                    Assign appropriate chain, store and role for user(s).
                </p>

                {apiErrorMessage && (
                    <div
                        style={{
                            padding: "12px 16px",
                            marginBottom: "16px",
                            borderRadius: 6,
                            border: "1px solid #FECACA",
                            backgroundColor: "#FEF2F2",
                            color: "#B91C1C",
                            fontSize: 13,
                        }}
                    >
                        <strong>Error:</strong> {apiErrorMessage}
                    </div>
                )}

                <div style={{ borderBottom: "1px solid #F0F2F5", marginBottom: "15px" }} />

                <div style={styles.emailBadge}>
                    <strong>Chain:</strong>{" "}
                    <span>
                        {selectedChainLabel !== "Select Chain" ? selectedChainLabel : "—"}
                    </span>
                </div>

                <div
                    style={{
                        ...styles.modalContentWrapper,
                        maxHeight: "60vh",
                        overflowY: "auto",
                    }}
                >
                    {/* Assignment Summary Table */}
                    <div style={styles.tableSectionWrapper}>
                        <p style={styles.tableSectionTitle}>Assignment Summary</p>
                        <LightThemeTable
                            data={tableData}
                            columns={tableColumns}
                            pageSizeOptions={[5, 10, 20]}
                            isLoading={false}
                        />
                    </div>
                </div>
            </CommonModal>
        );
    }
}

const connector = connect(
    (state: State) => ({
        chains: state.chains.content || [],
        stores: state.stores.content?.data || [],
        user: state.auth_user.content,
        roles: state.chains_users_role.roles,
    }),
    {
        fetchBasicInfoChains,
        fetchStores,
        getRoles,
        updateRequestApprovalStatus,
        createRequestAuditLog,
    }
);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(AssignChainStore);